/**
 * Role-hierarchy / level data hooks (S2.3).
 *
 * Same discipline as `useSystemGovernance`: reads via ahooks `useRequest` with
 * `/api/`-prefixed cacheKeys; mutations run through `systemApi` (threading the
 * session `actorName` so the handler writes its audit row server-side), clear
 * the affected cacheKeys — including the audit cache, so the review lens picks
 * the new row up — then the caller refreshes.
 *
 * The panel's pure projection (`buildHierarchyRows` — roles grouped by band
 * with resolver-derived counts) lives in the framework-free
 * `hierarchyProjection.ts` so the node-env vitest suite can exercise it; it is
 * re-exported here as the panel's single import surface.
 */

import { clearCache, useRequest } from 'ahooks';
import { getUserProfile } from '@/services/api/axios.config';
import type { User } from '@/models/user';
import { systemApi } from './systemApi';
import type { CreateRoleInput } from './systemApi';
import type { GovernanceRole, RoleLevel } from './model';

export {
  buildHierarchyRows,
  roleClosureNames,
  type HierarchyBand,
  type HierarchyRow,
} from './hierarchyProjection';

const LEVELS_KEY = '/api/governance/role-levels';
const ROLES_KEY = '/api/governance/roles';
const USERS_KEY = '/api/governance/users';
const AUDIT_KEY = '/api/governance/audit-logs';

const actorName = (): string | undefined => (getUserProfile() as User | null)?.name ?? undefined;

export function useRoleLevels() {
  const { data, error, loading, refresh } = useRequest(systemApi.listRoleLevels, {
    cacheKey: LEVELS_KEY,
  });
  return { levels: data?.data, error, loading, refresh };
}

/** Every S2.3 write lands an audit row server-side — invalidate the lens. */
function invalidateAudit() {
  clearCache(AUDIT_KEY);
}

export function useRoleHierarchyMutations() {
  return {
    createRole: async (input: CreateRoleInput) => {
      const role = await systemApi.createRole({ ...input, actorName: actorName() });
      clearCache(ROLES_KEY);
      invalidateAudit();
      return role;
    },

    setRoleHierarchy: async (role: GovernanceRole, inheritsFrom: string[], reason: string) => {
      const updated = await systemApi.setRoleHierarchy(role.id, inheritsFrom, reason, actorName());
      clearCache(ROLES_KEY);
      // An edge edit changes every holder's effective snapshot on restamp.
      clearCache(USERS_KEY);
      invalidateAudit();
      return updated;
    },

    createLevel: async (name: string) => {
      const level = await systemApi.createRoleLevel(name, actorName());
      clearCache(LEVELS_KEY);
      invalidateAudit();
      return level;
    },

    renameLevel: async (level: RoleLevel, name: string) => {
      const updated = await systemApi.renameRoleLevel(level.id, name, actorName());
      clearCache(LEVELS_KEY);
      invalidateAudit();
      return updated;
    },

    reorderLevel: async (level: RoleLevel, direction: 'up' | 'down') => {
      const list = await systemApi.reorderRoleLevel(level.id, direction, actorName());
      clearCache(LEVELS_KEY);
      // Bands are display-only: roles re-sort under the new ladder, grants never change.
      clearCache(ROLES_KEY);
      invalidateAudit();
      return list;
    },

    deleteLevel: async (level: RoleLevel) => {
      await systemApi.deleteRoleLevel(level.id, actorName());
      clearCache(LEVELS_KEY);
      invalidateAudit();
    },
  };
}
